<script setup lang="ts">
import { NInput } from 'naive-ui'
import { nextTick, ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CTableEditActionButtons from '@lib/components/CTableEditActionButtons/CTableEditActionButtons.vue'
import ClickOutsideDirective from '@lib/directives/clickOutside'
import { isPitcherOrIosDevice } from '@lib/util/userAgents'
import useThemeVars from '@lib/composables/useThemeVars'
import { highlight } from '@lib/util/string'

type State = 'view' | 'edit'

const props = withDefaults(
  defineProps<{
    maxLength?: number
    search?: string
    modelValue: string
    state?: State
    readonly?: boolean
    placeholder?: string
    valueClass?: string
    alwaysDisplayEditIcon?: boolean
    useFixedEditWidth?: boolean
  }>(),
  {
    search: '',
    state: 'view',
    useFixedEditWidth: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'edit'): void
  (e: 'cancel'): void
  (e: 'click', value: MouseEvent): void
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const vClickOutside = ClickOutsideDirective

const inputRef = ref<InstanceType<typeof NInput> | null>(null)
const textRef = ref<HTMLElement | null>(null)
const model = ref(props.modelValue)

const tooltipTrigger = ref(0)

const showTooltip = computed(() => {
  tooltipTrigger.value // Force reactivity
  if (!textRef.value) return false
  return textRef.value.scrollWidth > textRef.value.clientWidth
})

watch([() => props.modelValue, () => props.state], async ([newValue, newState]) => {
  if (newState === 'edit') {
    model.value = newValue
    await nextTick()
    inputRef.value?.focus()
  }

  if (newValue !== undefined) {
    await nextTick()
    tooltipTrigger.value++
  }
})

function onSave() {
  emit('update:modelValue', props.maxLength ? model.value.slice(0, props.maxLength) : model.value)
}

function onCancel() {
  model.value = props.modelValue
  emit('cancel')
}

function handleKey(event: KeyboardEvent) {
  switch (event.key) {
    case 'Enter':
      onSave()
      break
    case 'Escape':
      onCancel()
      break
    default:
      break
  }
}
</script>

<template>
  <div class="c-table-input" @click="emit('click', $event)">
    <div v-if="state === 'view'" class="flex items-center relative c-table-input--view flex-auto">
      <div class="h-full flex gap-1 items-center relative min-w-0 flex-1 c-table-input--view__value">
        <slot name="prefix" />

        <div
          ref="textRef"
          v-tooltip="showTooltip ? modelValue : null"
          :class="['overflow-hidden text-ellipsis whitespace-nowrap min-w-0', valueClass]"
          v-html="highlight(modelValue, search)"
        />
        <div
          v-if="!readonly && (!isPitcherOrIosDevice || alwaysDisplayEditIcon)"
          v-tooltip="t('canvasUI.common.edit')"
          class="cursor-pointer ml-1 flex-shrink-0 trigger-edit w-9 h-9 flex items-center justify-center"
          :class="{ 'edit-icon': !alwaysDisplayEditIcon }"
          @click.stop="emit('edit')"
        >
          <CIcon :color="themeVars.primary3" icon="pen" />
        </div>
      </div>
      <div class="flex-shrink-0">
        <slot name="suffix" />
      </div>
    </div>
    <div v-else-if="state === 'edit'" class="flex items-center gap-[2px] c-table-input--edit flex-auto">
      <slot name="prefix" />
      <NInput
        ref="inputRef"
        v-model:value="model"
        v-click-outside="onSave"
        :class="[useFixedEditWidth ? 'edit-input' : '']"
        :placeholder="placeholder ?? t('canvasUI.components.tables.inputTypePlaceholder')"
        @click.stop
        @keydown="handleKey"
      />
      <CTableEditActionButtons @cancel="onCancel" @save="onSave" />
    </div>
  </div>
</template>

<style lang="scss">
.c-table-input {
  .edit-icon {
    visibility: hidden;
  }

  &:hover {
    .edit-icon {
      visibility: visible;
    }
  }

  &--view {
    min-width: 0;

    &__value {
      font-size: 14px;
      line-height: normal;
      letter-spacing: 0.4px;
      min-width: 0;
    }
  }

  &--edit {
    .edit-input {
      width: 300px;
    }
  }
}
</style>
